/**
 * Icon-only action button. Replaces the tiny underlined text links that
 * dotted the member/circle rows — a glyph reads cleaner and the 44px
 * (w-11 h-11) hit-area meets Apple's minimum touch target while the 24px
 * glyph stays legible at arm's length on a phone.
 *
 * `label` is required: it's both the screen-reader name (aria-label) and
 * the desktop hover tooltip (title), since there's no visible text. The
 * `destructive` variant stays neutral at rest and reveals oxblood on
 * interaction — matching the existing trash affordance in routes/photo.tsx.
 *
 * The glyph size is normalized here (via cloneElement) so every call site
 * gets one consistent, touch-optimized size regardless of the icon's own
 * default — pass a plain `<IconX />` and don't set `size` yourself.
 */
import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';

/** Glyph size inside the standard button. Tuned for phone legibility. */
export const ICON_BUTTON_GLYPH = 24;

interface IconButtonProps {
  /** An <IconX /> element from components/icons.tsx. */
  icon: ReactNode;
  /** Required — used as aria-label AND the hover tooltip. */
  label: string;
  onClick?: () => void;
  variant?: 'default' | 'destructive';
  /** Swaps the glyph for a spinner and disables the button. */
  busy?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
}

export function IconButton({
  icon,
  label,
  onClick,
  variant = 'default',
  busy = false,
  disabled = false,
  type = 'button',
}: IconButtonProps) {
  const tone =
    variant === 'destructive'
      ? 'text-soft hover:text-oxblood hover:bg-oxblood/10 active:bg-oxblood/15'
      : 'text-soft hover:text-ink hover:bg-line/40 active:bg-line/60';
  const sized = isValidElement(icon)
    ? cloneElement(icon as ReactElement<{ size?: number }>, { size: ICON_BUTTON_GLYPH })
    : icon;
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      aria-busy={busy || undefined}
      onClick={onClick}
      disabled={disabled || busy}
      className={`inline-flex items-center justify-center w-11 h-11 rounded-full transition-colors disabled:opacity-50 ${tone}`}
    >
      {busy ? (
        <span
          className="block w-6 h-6 rounded-full border-2 border-current border-t-transparent animate-spin"
          aria-hidden
        />
      ) : (
        sized
      )}
    </button>
  );
}
